import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Briefcase,
  Compass,
  Lightbulb,
  Sparkles,
  Target,
  GraduationCap,
  Globe2,
  Network,
  BarChart3,
  Users,
  Mail,
  MapPin,
} from "lucide-react";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";

const RESPONSIBILITIES = [
  "Mitarbeit in Projektteams für Digitalisierung, KI, Prozessoptimierung und Supply-Chain-Transformation",
  "Analyse von Geschäftsprozessen, Wertströmen und Datenstrukturen unserer Klienten",
  "Erarbeitung von Lösungs- und Optimierungskonzepten in enger Abstimmung mit Senior Consultants",
  "Vorbereitung von Workshops, Präsentationen und Entscheidungsvorlagen für Geschäftsführungen",
  "Markt- und Wettbewerbsanalysen für nationale und internationale Industrieprojekte",
  "Mitwirkung beim Aufbau und der Pflege strategischer Netzwerke (Industrie, Public Sector, Verbände)",
];

const PROFILE = [
  "Abgeschlossenes oder kurz vor dem Abschluss stehendes Studium (Wirtschaft, Wirtschaftsingenieurwesen, Informatik, MBA o. ä.)",
  "Erste Praktika oder Projekterfahrung in Beratung, Industrie oder Start-up von Vorteil",
  "Analytisches Denken, strukturierte Arbeitsweise und unternehmerisches Mindset",
  "Begeisterung für Digitalisierung, KI, Robotik und industrielle Transformation",
  "Sehr gute Deutsch- und Englischkenntnisse, Reisebereitschaft innerhalb DACH/EU",
];

const OFFER = [
  "Steile Lernkurve in einer wachstumsstarken Beratungsboutique",
  "Direkte Mitarbeit an strategischen Projekten ab Tag 1 – kein Junior-Schubladendenken",
  "Persönliches Mentoring durch Geschäftsführung und Senior Consultants",
  "Strukturiertes Trainee-Programm: Method Tracks, Tech Days, Networking Events",
  "Hybrid Working, modernes Equipment, attraktives Vergütungspaket",
  "Klare Entwicklungspfade Richtung Senior Consultant / Manager",
];

const JOURNEY = [
  {
    icon: Compass,
    title: "Onboarding & Foundations",
    text: "Strukturierter Einstieg in Beratungsmethoden, Tools und das X10 Operating Model.",
  },
  {
    icon: BarChart3,
    title: "Projekteinsätze",
    text: "Mitarbeit in interdisziplinären Teams – Digitalisierung, KI, Lean, Supply Chain, Defense Market Access.",
  },
  {
    icon: Lightbulb,
    title: "Skill Tracks",
    text: "Vertiefungen u.a. in Process Mining, Generative AI, Industrial Operations, Strategy.",
  },
  {
    icon: Target,
    title: "Ownership & Senior Track",
    text: "Schrittweise Übernahme von Workstream- und (Teil-)Projektverantwortung Richtung Consultant / Senior.",
  },
];

const FOCUS = [
  { icon: Briefcase, label: "Business Transformation" },
  { icon: Sparkles, label: "AI & Analytics" },
  { icon: Network, label: "Supply Chain & Nearshoring" },
  { icon: GraduationCap, label: "Public Sector & Defense" },
  { icon: Globe2, label: "International Networks" },
  { icon: Users, label: "Operational Excellence" },
];

export default function TraineeConsultant() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="career-page" className="font-body bg-white text-slate-900 min-h-screen">
      <SEO
        title="Trainee Consultant (m/w/d) – Business Transformation & Operational Excellence | X10 Management"
        description="Werde Teil von X10 Management. Trainee Consultant Position mit Fokus auf Business Transformation, KI, Supply Chain und Operational Excellence. Bochum / DACH / EU."
      />

      {/* Header */}
      <header className="border-b border-slate-100 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="font-heading font-bold text-lg sm:text-xl text-[#0A192F]">
            X10 Management GmbH
          </Link>
          <Link
            to="/#karriere"
            className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-[#0A192F] transition-colors"
          >
            <ArrowLeft size={16} /> Karriere
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden bg-[#0A192F] text-white">
        <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full bg-[#C9A96E]/15 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-40 -left-40 w-[520px] h-[520px] rounded-full bg-[#1E3A8A]/40 blur-3xl pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-extrabold text-[#C9A96E]">
            <span className="w-1.5 h-1.5 rounded-full bg-[#C9A96E]" />
            Open Position · Trainee Programme
          </span>
          <h1 className="mt-5 font-heading font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] max-w-4xl">
            Trainee Consultant <span className="text-[#C9A96E]">(m/w/d)</span>
          </h1>
          <p className="mt-4 font-heading text-xl sm:text-2xl text-white/85 max-w-3xl">
            Business Transformation & Operational Excellence
          </p>

          <div className="mt-8 flex flex-wrap gap-3 text-sm text-slate-300">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10">
              <MapPin size={14} className="text-[#C9A96E]" /> Bochum · DACH · EU
            </span>
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10">
              <Briefcase size={14} className="text-[#C9A96E]" /> Vollzeit
            </span>
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10">
              <Sparkles size={14} className="text-[#C9A96E]" /> Direkter Einstieg / Berufseinstieg
            </span>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 grid lg:grid-cols-12 gap-12">
        <main className="lg:col-span-8 space-y-16">
          {/* Overview */}
          <section data-testid="career-overview">
            <SectionTitle eyebrow="Overview" title="Deine Rolle bei X10" />
            <p className="mt-6 text-lg leading-relaxed text-slate-700">
              Als Trainee Consultant arbeitest du vom ersten Tag an in unseren Projektteams an
              echten Transformationsvorhaben für mittelständische Unternehmen und Konzerne. Du
              lernst, wie sich Strategie, Prozesse, Daten und KI zu messbaren Wettbewerbsvorteilen
              verbinden lassen – fachlich anspruchsvoll, persönlich begleitet, mit klarem
              Verantwortungspfad.
            </p>
            <div className="mt-8 flex flex-wrap gap-2">
              {FOCUS.map(({ icon: Icon, label }) => (
                <span
                  key={label}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#0A192F]/5 border border-[#C9A96E]/30 text-xs uppercase tracking-[0.18em] font-semibold text-[#0A192F]"
                >
                  <Icon size={12} className="text-[#1E3A8A]" />
                  {label}
                </span>
              ))}
            </div>
          </section>

          {/* Journey */}
          <section data-testid="career-journey">
            <SectionTitle eyebrow="Your Learning Journey" title="Vom Trainee zum Consultant" />
            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              {JOURNEY.map(({ icon: Icon, title, text }, i) => (
                <motion.div
                  key={title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  className="group p-6 rounded-2xl bg-[#0A192F] text-white border border-[#0A192F] hover:border-[#3B82F6]/70 hover:shadow-[0_0_30px_rgba(59,130,246,0.25)] transition-all duration-300"
                >
                  <div className="w-11 h-11 rounded-full bg-white/10 border border-[#C9A96E]/30 flex items-center justify-center">
                    <Icon size={18} className="text-[#C9A96E]" />
                  </div>
                  <div className="mt-4 font-heading text-lg font-semibold group-hover:text-[#93C5FD] transition-colors">
                    {title}
                  </div>
                  <p className="mt-2 text-sm text-slate-300 leading-relaxed">{text}</p>
                </motion.div>
              ))}
            </div>
          </section>

          {/* Responsibilities */}
          <BulletSection
            testId="career-responsibilities"
            eyebrow="Responsibilities"
            title="Was dich erwartet"
            items={RESPONSIBILITIES}
          />

          {/* Profile */}
          <BulletSection
            testId="career-profile"
            eyebrow="What we are looking for"
            title="Dein Profil"
            items={PROFILE}
          />

          {/* Offer */}
          <BulletSection
            testId="career-offer"
            eyebrow="What we offer"
            title="Was wir bieten"
            items={OFFER}
          />

          {/* Application CTA */}
          <section
            data-testid="career-apply"
            className="relative overflow-hidden rounded-3xl bg-[#0A192F] text-white p-8 sm:p-12"
          >
            <div className="absolute -top-32 -right-32 w-72 h-72 rounded-full bg-[#C9A96E]/20 blur-3xl pointer-events-none" />
            <div className="relative">
              <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-extrabold text-[#C9A96E]">
                <span className="w-1.5 h-1.5 rounded-full bg-[#C9A96E]" /> Application
              </span>
              <h3 className="mt-4 font-heading text-2xl sm:text-3xl font-bold leading-tight">
                Bereit, den Mittelstand mitzugestalten?
              </h3>
              <p className="mt-4 text-slate-300 leading-relaxed max-w-2xl">
                Schick uns deinen CV und ein kurzes Motivationsschreiben. Wir freuen uns auf
                Persönlichkeiten – Lebensläufe sind eine Momentaufnahme, deine Geschichte ist mehr
                wert.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href="mailto:karriere@x10management.de?subject=Bewerbung%20Trainee%20Consultant%20(m%2Fw%2Fd)"
                  className="group inline-flex items-center gap-2 px-7 py-4 rounded-full bg-white text-[#0A192F] font-semibold hover:bg-[#C9A96E] hover:text-white transition-all duration-300"
                  data-testid="career-apply-email"
                >
                  <Mail size={18} /> karriere@x10management.de
                </a>
                <Link
                  to="/#contact"
                  className="inline-flex items-center gap-2 px-7 py-4 rounded-full border border-white/30 font-semibold hover:bg-white/10 transition-all duration-300"
                >
                  Fragen? Sprich uns an
                </Link>
              </div>
            </div>
          </section>
        </main>

        {/* Sidebar */}
        <aside className="lg:col-span-4 space-y-6 lg:sticky lg:top-28 self-start">
          <FactCard
            label="Standort"
            value="Bochum (HQ) · Hybrid · Reisen in DACH & EU"
            icon={MapPin}
          />
          <FactCard label="Vertragsart" value="Vollzeit · Unbefristet" icon={Briefcase} />
          <FactCard
            label="Start"
            value="Nach Vereinbarung – wir warten auf die Richtigen"
            icon={Sparkles}
          />
          <FactCard
            label="Vergütung"
            value="Wettbewerbsfähig · Performance-Komponente"
            icon={BarChart3}
          />
          <div className="rounded-2xl p-6 bg-[#F3F4F6] border border-slate-200">
            <div className="text-xs uppercase tracking-[0.25em] font-extrabold text-[#1E3A8A]">
              Diversity & Mindset
            </div>
            <p className="mt-3 text-sm text-slate-700 leading-relaxed">
              Wir setzen auf Vielfalt: Hintergründe, Disziplinen und Perspektiven sind unser
              wichtigstes Kapital. Bewerbungen aller qualifizierten Personen sind ausdrücklich
              willkommen.
            </p>
          </div>
        </aside>
      </div>

      <Footer />
    </div>
  );
}

function SectionTitle({ eyebrow, title }) {
  return (
    <div>
      <span className="text-sm uppercase tracking-[0.25em] font-extrabold text-[#1E3A8A]">{eyebrow}</span>
      <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-bold text-[#0A192F] tracking-tight leading-tight">
        {title}
      </h2>
    </div>
  );
}

function BulletSection({ eyebrow, title, items, testId }) {
  return (
    <section data-testid={testId}>
      <SectionTitle eyebrow={eyebrow} title={title} />
      <ul className="mt-8 space-y-3">
        {items.map((item, i) => (
          <motion.li
            key={i}
            initial={{ opacity: 0, x: -10 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: i * 0.05 }}
            className="flex items-start gap-4 text-slate-700 leading-relaxed"
          >
            <span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-[#C9A96E] flex-shrink-0" />
            <span>{item}</span>
          </motion.li>
        ))}
      </ul>
    </section>
  );
}

function FactCard({ label, value, icon: Icon }) {
  return (
    <div className="rounded-2xl p-6 bg-white border border-slate-200 shadow-sm">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-[#0A192F]/5 border border-[#C9A96E]/30 flex items-center justify-center">
          <Icon size={16} className="text-[#0A192F]" />
        </div>
        <div className="text-xs uppercase tracking-[0.2em] font-extrabold text-[#1E3A8A]">{label}</div>
      </div>
      <div className="mt-3 text-slate-800 font-medium">{value}</div>
    </div>
  );
}
